/**
 * 👤 ONE naming rule, for every surface that shows a person.
 *
 * Three different questions get three different answers, and conflating
 * them is what produced "Hi azizmeha" on a signed-in dashboard:
 *
 *   greetingName  — what you CALL someone. First name.
 *   fullName      — how you IDENTIFY them to a human. "First Last".
 *   handle        — how the SYSTEM identifies them. "@username", unique.
 *
 * ── Why a shared module ─────────────────────────────────────────────
 *
 * Every call site had its own fallback chain, and they disagreed. The
 * dashboard header used
 * `firstName || name || username || email` — so an account with no first
 * name greeted the person by their login handle, and one with no username
 * greeted them by their EMAIL ADDRESS, on a screen they might be sharing.
 *
 * The rules below are deliberate:
 *
 *   • An email address is NEVER a name. It is a credential, it is often
 *     personal, and "Hi mokter897@gmail.com" reads as a mail merge that
 *     failed. If nothing else is known we say "there" — a greeting that
 *     admits it does not know you is better than one that gets you wrong.
 *
 *   • A username is a fallback for identity, never for greeting. "@azizmeha"
 *     is a correct way to REFER to someone and a poor way to address them.
 *
 *   • Nothing is title-cased or otherwise "corrected". People capitalise
 *     their own names, including lowercase ones, and a system that tidies
 *     them is a system that gets names wrong for a living.
 */

const clean = (value) => String(value ?? '').trim();

/**
 * The name parts we hold, wherever they live on the record.
 *
 * Normalised HERE rather than relying on the `= {}` defaults each export
 * declares, because a default parameter fires on `undefined` and NOT on
 * `null` — and a signed-out visitor is `null`, not missing. Every export in
 * this file had the same hole, and it surfaced as a guest opening the review
 * modal and getting a crash page instead of a form.
 *
 * One guard at the single place they all funnel through: fixing each caller
 * would leave the next one to rediscover it.
 */
const partsOf = (input) => {
  const user = (input && typeof input === 'object') ? input : {};
  const about = (user.about && typeof user.about === 'object') ? user.about : {};
  return {
    first: clean(user.firstName || about.firstName),
    last: clean(user.lastName || about.lastName),
    // A single free-text name, used by some older records and by OAuth
    // profiles that only ever return one string.
    whole: clean(user.name || user.fullName || about.displayName || user.displayName),
    username: clean(user.username),
    email: clean(user.email)
  };
};

/**
 * What to CALL them. First name only.
 *
 * @param {object} user
 * @param {string} [fallback='there'] used when we genuinely do not know.
 */
export const greetingName = (user, fallback = 'there') => {
  const p = partsOf(user);
  if (p.first) return p.first;
  // A whole name we cannot split: take the first token, which is the
  // first name for the overwhelming majority of the world's name orders,
  // and is at worst the name they chose to lead with.
  if (p.whole) return p.whole.split(/\s+/)[0];
  if (p.username) return p.username;
  // Deliberately NOT the email. See the header.
  return fallback;
};

/**
 * How to IDENTIFY them to a human. "First Last" where known.
 *
 * Falls back through a whole name, then the username. Never the email.
 */
export const fullName = (user, fallback = 'Unnamed') => {
  const p = partsOf(user);
  const joined = [p.first, p.last].filter(Boolean).join(' ');
  if (joined) return joined;
  if (p.whole) return p.whole;
  if (p.username) return p.username;
  return fallback;
};

/**
 * How the SYSTEM identifies them — the unique handle.
 *
 * `username` is the platform's unique identifier (the model carries a
 * derived `usernameLower` as the uniqueness key), so this is what
 * mentions, directories and links should key on. Returns null rather
 * than a guess when there is no username: a fake handle that does not
 * resolve is worse than an absent one.
 */
export const handle = (user) => {
  // Reads through partsOf like everything else in this file. It previously
  // reached for `user.username` directly, so the null guard added there did
  // not cover it — which a probe over null/0/'x'/[] found immediately, and
  // reading the code did not.
  const { username } = partsOf(user);
  return username ? `@${username.replace(/^@+/, '')}` : null;
};

/**
 * Name plus handle, for lists where two people may share a name.
 * "Mokter Hossain (@mokter)" — or just the name when there is no handle.
 */
export const nameWithHandle = (user) => {
  const name = fullName(user);
  const h = handle(user);
  return h && h !== `@${name}` ? `${name} (${h})` : name;
};

/** True when we hold enough to address someone by name. */
export const hasRealName = (user) => {
  const p = partsOf(user);
  return Boolean(p.first || p.whole);
};

/**
 * Their picture — the ONE chain, for every surface that shows a face.
 *
 * Four call sites had four different chains and they disagreed:
 *
 *   identity-menu.block      about.photoUrl → profilePictureUrl → avatarUrl → pictureUrl
 *   PublicProfile            about.photoUrl → avatarUrl
 *   Profile                  avatarUrl → … → user.avatarUrl
 *   reviewerProfileCompleteness  avatarUrl → about.avatarUrl
 *
 * So whether a person "had a picture" depended on which screen asked —
 * and reviewer profile-completeness could score someone complete on a
 * field the header would not render. `about.photoUrl` is what the upload
 * writes and therefore leads; the rest are shapes older records and OAuth
 * profiles arrive in.
 *
 * Returns '' when there is nothing to show, so a caller can fall through
 * to initials with a plain falsy check.
 *
 * ── Why the scheme check ────────────────────────────────────────────
 *
 * This value is user-controlled, crosses a service boundary, and lands in
 * an `<img src>`. Only http(s) and site-relative paths are let through:
 * anything else — `javascript:`, `data:`, a bare `//host` that silently
 * changes origin — is treated as no picture at all. A missing avatar is a
 * cosmetic loss; an unchecked URL from another user's record is not.
 */
export const photoUrl = (input) => {
  const user = (input && typeof input === 'object') ? input : {};
  const about = (user.about && typeof user.about === 'object') ? user.about : {};

  const candidate = clean(
    about.photoUrl || about.avatarUrl || about.pictureUrl
    || user.photoUrl || user.avatarUrl || user.profilePictureUrl
    // `picture` / `image` are what OAuth providers return.
    || user.pictureUrl || user.picture || user.image
  );
  if (!candidate) return '';

  // Root-relative is ours ("/uploads/…"), and safe. Protocol-relative
  // ("//evil.example") is NOT — it reads like a path and is not one.
  if (candidate.startsWith('//')) return '';
  if (candidate.startsWith('/')) return candidate;
  return /^https?:\/\//i.test(candidate) ? candidate : '';
};

/** True when we hold a picture we are willing to render. */
export const hasPhoto = (user) => Boolean(photoUrl(user));



/**
 * 🕳️ WHAT TO CALL SOMEBODY WHO IS NOT THERE.
 *
 * A thread outlives its participants. An account can be erased under GDPR,
 * purged by the retention sweep 30 days later, or simply never have existed
 * — a stale id written into `participants` by an older code path. The
 * messages are still real, and the person still opening them still needs a
 * sentence at the top of the row.
 *
 * What they used to get was the id, shortened: "6a0568…79be", with the
 * monogram "6". That is the same mistake `projectName` exists to stop — a
 * bare id in a name slot reads as a real contact, cannot be searched for,
 * and makes several such rows indistinguishable from one another.
 *
 * Two states, because they are two different sentences and only one of them
 * is about a person who was ever here:
 *
 *   former_member — the account existed and is gone (deleted / anonymised).
 *                   You did talk to somebody. They left.
 *   unknown       — no account has ever answered to this id, or it was
 *                   purged past our retention window. Nobody can be named.
 *
 * The STATE crosses service boundaries; the WORDS are resolved here, once,
 * so an inbox, a reading pane and a moderation feed cannot drift into three
 * vocabularies for the same absence.
 */
export const PERSON_STATE = Object.freeze({
  FORMER_MEMBER: 'former_member',
  UNKNOWN: 'unknown'
});

const PERSON_STATE_LABELS = Object.freeze({
  [PERSON_STATE.FORMER_MEMBER]: 'Former member',
  [PERSON_STATE.UNKNOWN]: 'Unknown participant'
});

/**
 * The words for a state. Anything unrecognised — including nothing at all —
 * is "unknown": a label we cannot justify is not a licence to fall back to
 * the id.
 */
export const personStateLabel = (state) => (
  PERSON_STATE_LABELS[String(state || '')] || PERSON_STATE_LABELS[PERSON_STATE.UNKNOWN]
);

// A rendered label can name SEVERAL people — "Mokter Hossain, azizmeha", or
// "A, B…" once a group runs past two. Split it back into parts so a mixed
// group is not judged by its first one.
const labelParts = (label) => String(label || '')
  .replace(/…\s*$/, '')
  .split(',')
  .map((part) => part.trim())
  .filter(Boolean);

/**
 * The parts of a label that are actually somebody's name.
 *
 * Surfaces that derive something FROM a name — a monogram, a mention token,
 * a search index — ask this rather than reading the label's first character.
 * "Unknown participant" must not become the monogram "U", which looks
 * exactly like a person's; and on "Unknown participant, persona-rev-l20"
 * the letter worth showing belongs to the person who is really there.
 */
export const realNamesIn = (label) => labelParts(label)
  .filter((part) => !Object.values(PERSON_STATE_LABELS).includes(part));

/** True when a label names nobody at all — every part of it is a placeholder. */
export const isPersonStateLabel = (label) => {
  const parts = labelParts(label);
  return parts.length > 0 && realNamesIn(label).length === 0;
};

export default {
  greetingName, fullName, handle, nameWithHandle, hasRealName, photoUrl, hasPhoto,
  PERSON_STATE, personStateLabel, isPersonStateLabel, realNamesIn
};
